import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Snackbar,
    Grid,
    Divider,
    Paper
} from '@mui/material';
import { Info as InfoIcon, Warning as WarningIcon } from '@mui/icons-material';
import { getQuizConfig, saveQuizConfig, resolveQuizConfig, QuizConfigDTO, ResolvedQuizConfigurationDTO, QuizConfigLevel } from '../../services/quizService';

interface QuizConfigurationModalProps {
    open: boolean;
    onClose: () => void;
    level: QuizConfigLevel;
    entityId: number;
    entityName?: string;
    /** Resolution context. `subjectId` is treated as `curriculumSubjectId` (legacy alias). */
    sourceContext?: {
        courseId?: number;
        topicId?: number;
        subjectId?: number;
        curriculumSubjectId?: number;
        coreSubjectId?: number;
    };
    autoEdit?: boolean; // If true, opens directly in edit mode
}

const QuizConfigurationModal: React.FC<QuizConfigurationModalProps> = ({
    open,
    onClose,
    level,
    entityId,
    entityName,
    sourceContext,
    autoEdit = false
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // States to track "What is currently active" vs "What is stored locally"
    const [localConfig, setLocalConfig] = useState<QuizConfigDTO | null>(null);
    const [effectiveConfig, setEffectiveConfig] = useState<ResolvedQuizConfigurationDTO | null>(null);

    // Form State
    const [formData, setFormData] = useState<QuizConfigDTO>({
        maxQuestions: 10,
        timeLimitMinutes: 20,
        minPassPercent: 70,
        maxRetries: 3,
        allowAiGeneration: true
    });

    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open, level, entityId]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        setEditMode(autoEdit); // Respect autoEdit prop
        try {
            // 1. Fetch Local Config
            const localRes = await getQuizConfig(level, entityId);
            const isLocalConfigPresent = localRes.maxQuestions != null;

            if (isLocalConfigPresent) {
                setLocalConfig(localRes);
                setFormData(localRes);
                // If local config exists, it IS the effective config
                setEffectiveConfig({
                    ...localRes,
                    source: level,
                    sourceId: entityId
                } as ResolvedQuizConfigurationDTO);
            } else {
                setLocalConfig(null);
                // 2. Fetch Effective Config (Resolution)
                // If we are configuring a Subject, there's no "Course" context, just Subject > System.
                // But the resolution endpoint is generic.

                let cId = sourceContext?.courseId;
                let tId = sourceContext?.topicId;
                let curSubId = sourceContext?.curriculumSubjectId ?? sourceContext?.subjectId;
                let coreSubId = sourceContext?.coreSubjectId;

                // If configuring a specific entity, it IS the context.
                if (level === 'COURSE') cId = entityId;
                if (level === 'TOPIC') tId = entityId;
                if (level === 'SUBJECT' || level === 'CURRICULUM_SUBJECT') curSubId = entityId;
                if (level === 'CORE_SUBJECT') coreSubId = entityId;

                const resolved = await resolveQuizConfig(cId, tId, curSubId, coreSubId);
                setEffectiveConfig(resolved);
                // Pre-fill form with effective values for ease of editing
                setFormData({
                    maxQuestions: resolved.maxQuestions,
                    timeLimitMinutes: resolved.timeLimitMinutes,
                    minPassPercent: resolved.minPassPercent,
                    maxRetries: resolved.maxRetries,
                    allowAiGeneration: resolved.allowAiGeneration
                });
            }
        } catch (err: any) {
            console.error("Failed to load config", err);
            // If we are in autoEdit mode (creating new), failing to load effective config shouldn't block.
            // But if we fail to load LOCAL config, that's ambiguous.
            // Assumption: if error, treat as "No config found" and allow user to create.
            if (!autoEdit) {
                setError("Failed to load configuration details. You can still create a new one.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            await saveQuizConfig(level, entityId, formData);
            setSuccessMsg("Configuration saved successfully.");
            setTimeout(() => {
                onClose();
                setSuccessMsg(null);
            }, 1500); // Wait a bit to show success before closing, or close immediately and show global toast?
            // User asked: "on save please do not show pop message, print message on the screen which should fade in 15 secs"
            // "On success, modal closes + toast message"
            // I'll close and expect parent to show toast or I show toast here but logic implies modal closes. ok.

            onClose();
            // Trigger a global snackbar? Re-using local snackbar state won't work if modal closes. 
            // I'll pass a generic onClose(true) to indicate success? 
            // For now, I'll rely on the parent or a global context if available. 
            // But simple implementation: I'll use a local Snackbar that persists? No, it's modal.
            // HACK: I'll keep modal open for 1 sec with success alert then close.
        } catch (err: any) {
            // "Modal stays open. Error message displayed inline"
            setError(err.response?.data?.message || "Failed to save configuration.");
            setLoading(false);
        }
    };

    const handleDeleteLocal = async () => {
        // Future: Allow "Reset to Default" (DELETE)
        alert("Resetting to default not yet implemented in UI");
    };

    if (loading && !formData.maxQuestions) { // Initial load
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogContent><CircularProgress /></DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Quiz Configuration: {entityName || level}
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {/* View Mode: Show Effective Config Summary */}
                {!editMode && effectiveConfig && (
                    <Box>
                        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                            {localConfig ? (
                                <Typography variant="body2">
                                    This {level.toLowerCase()} has its own customized settings.
                                </Typography>
                            ) : (
                                <Typography variant="body2">
                                    Using settings inherited from <strong>{effectiveConfig.source}</strong> (ID: {effectiveConfig.sourceId}).
                                </Typography>
                            )}
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">Max Questions</Typography>
                                    <Typography variant="h6">{effectiveConfig.maxQuestions}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">Time Limit</Typography>
                                    <Typography variant="h6">{effectiveConfig.timeLimitMinutes} min</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">Pass Score</Typography>
                                    <Typography variant="h6">{effectiveConfig.minPassPercent}%</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary">AI Generation</Typography>
                                    <Typography variant="h6">{effectiveConfig.allowAiGeneration ? "Enabled" : "Disabled"}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>
                )}

                {/* Edit Mode Form */}
                {editMode && (
                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Max Questions"
                                    type="number"
                                    fullWidth
                                    value={formData.maxQuestions || ''}
                                    onChange={(e) => setFormData({ ...formData, maxQuestions: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Time Limit (Minutes)"
                                    type="number"
                                    fullWidth
                                    value={formData.timeLimitMinutes || ''}
                                    onChange={(e) => setFormData({ ...formData, timeLimitMinutes: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Min Pass %"
                                    type="number"
                                    fullWidth
                                    value={formData.minPassPercent || ''}
                                    onChange={(e) => setFormData({ ...formData, minPassPercent: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Max Retries"
                                    type="number"
                                    fullWidth
                                    value={formData.maxRetries || ''}
                                    onChange={(e) => setFormData({ ...formData, maxRetries: parseInt(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.allowAiGeneration || false}
                                            onChange={(e) => setFormData({ ...formData, allowAiGeneration: e.target.checked })}
                                        />
                                    }
                                    label="Allow AI Generated Questions"
                                />
                                <Typography variant="caption" display="block" color="textSecondary">
                                    If enabled, students can generate unlimited practice quizzes using AI.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                {!editMode ? (
                    <Button onClick={() => setEditMode(true)} variant="contained" color="primary">
                        {localConfig ? "Edit Configuration" : "Override Configuration"}
                    </Button>
                ) : (
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default QuizConfigurationModal;
